import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { GlassCard } from '../../components/GlassCard';
import BrandLogo from '../../components/BrandLogo';
import { Printer, Download, TrendingUp } from 'lucide-react';
import { arrayToCsvBlob, downloadBlob } from '../../lib/csv';

const PERIODS = ['30d', '90d', 'ytd'];

export default function ProfitAndLoss() {
  const { token, tenant } = useAuth();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    api.erpPnL(token, period).then(setData).catch(e => { if (e.status !== 401) toast.show(e.message, 'error'); }).finally(() => setLoading(false));
  }, [token, period, toast]);

  const exportCsv = () => {
    if (!data) return;
    const csvRows = [
      { line: 'Revenue', amount: data.revenue },
      { line: 'COGS', amount: data.cogs },
      { line: 'Gross Profit', amount: data.gross_profit },
      ...(data.operating_expenses || []).map(e => ({ line: `OpEx: ${e.category}`, amount: e.amount })),
      { line: 'Operating Expenses Total', amount: data.operating_expenses_total },
      { line: 'Net Profit', amount: data.net_profit },
    ];
    downloadBlob(arrayToCsvBlob(csvRows, ['line', 'amount'], ['Line Item', 'Amount']), `pnl-${period}-${new Date().toISOString().slice(0,10)}.csv`);
    toast.show('CSV downloaded', 'success');
  };

  const kpis = data ? [
    { label: 'Revenue', value: data.revenue, color: '#10B981' },
    { label: 'COGS', value: data.cogs, color: '#F59E0B' },
    { label: 'Gross Profit', value: data.gross_profit, color: '#14B8A6' },
    { label: 'OpEx Total', value: data.operating_expenses_total, color: '#EF4444' },
    { label: 'Net Profit', value: data.net_profit, color: data.net_profit >= 0 ? '#10B981' : '#EF4444' },
  ] : [];
  const marginPct = data?.revenue > 0 ? ((data.net_profit / data.revenue) * 100).toFixed(1) : '0.0';
  const grossPct = data?.revenue > 0 ? ((data.gross_profit / data.revenue) * 100).toFixed(1) : '0.0';
  const expenses = data?.operating_expenses || [];

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="report-pnl-page">
      <div className="hidden print:block mb-6">
        <div className="flex items-center gap-3 mb-2">
          <BrandLogo size={32} theme="light" />
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">{tenant?.name || '1000AD'}</h1>
            <p className="text-gray-500 text-xs">Profit & Loss ({period.toUpperCase()}) · Generated {new Date().toLocaleDateString('en-IN')}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-5 print:hidden">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
            <TrendingUp size={20} className="text-emerald-600" />
          </div>
          <h1 className="text-gray-900 font-extrabold text-xl">Profit & Loss</h1>
        </div>
        <div className="flex items-center gap-2">
          {PERIODS.map(p => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`pnl-period-${p}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${period === p ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'}`}>
              {p.toUpperCase()}
            </button>
          ))}
          <div className="w-px h-6 bg-gray-200 mx-1" />
          <button onClick={exportCsv} disabled={!data} data-testid="pnl-export-csv" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-40"><Download size={14} /> CSV</button>
          <button onClick={() => window.print()} data-testid="pnl-print" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-purple text-white text-sm font-semibold hover:opacity-90"><Printer size={14} /> Print</button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : data ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {kpis.map(k => (
              <GlassCard key={k.label} className="p-4">
                <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">{k.label}</p>
                <p className="font-extrabold text-lg tabular-nums mt-1" style={{ color: k.color }}>{formatMoney(k.value)}</p>
              </GlassCard>
            ))}
          </div>
          <div className="flex gap-2">
            <span className="px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700 text-xs font-bold tabular-nums">Net Margin: {marginPct}%</span>
            <span className="px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700 text-xs font-bold tabular-nums">Gross Margin: {grossPct}%</span>
          </div>
          {expenses.length > 0 && (
            <GlassCard className="overflow-hidden" data-testid="pnl-expenses-card">
              <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Operating Expenses by Category</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pnl-expenses-table">
                  <tbody>
                    {expenses.map((e, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50/60">
                        <td className="px-5 py-3 text-gray-900 font-semibold">{e.category}</td>
                        <td className="px-5 py-3 text-right text-gray-700 tabular-nums font-bold">{formatMoney(e.amount)}</td>
                        <td className="px-5 py-3 text-right text-gray-400 tabular-nums text-xs">{data.operating_expenses_total > 0 ? ((e.amount / data.operating_expenses_total) * 100).toFixed(1) : 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </GlassCard>
          )}
        </div>
      ) : (
        <GlassCard className="p-12 text-center"><p className="text-gray-500">No P&L data.</p></GlassCard>
      )}
    </div>
  );
}
